import { useCallback, useEffect, useRef, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { Link } from "react-router-dom";
import { Mic2, Upload, Trash2, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { API, getLeadRecordings, uploadRecording, deleteRecording, rescoreRecording, retranscribeRecording } from "@/lib/api";

const AUDIO_ACCEPT = "audio/*,.mp3,.wav,.m4a,.webm,.mp4,.mpeg,.ogg,.flac";
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
const mb = (b) => (b ? `${(b / (1024 * 1024)).toFixed(1)} MB` : "");
const mins = (s) => (s ? `${Math.round(s / 60)} min` : "");
const WORKING = ["pending", "transcribing", "queued_rating", "scoring"];
const scoreTone = (n) => (n >= 75 ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
  : n >= 55 ? "text-amber-300 border-amber-500/40 bg-amber-500/10" : "text-rose-300 border-rose-500/40 bg-rose-500/10");

function Progress({ r, data, onRetry, canPick }) {
  if (r.private) {
    // Another rep's call: the analysis is private to them and managers.
    return <div className="text-xs text-zinc-500">The recording and its analysis are private to {r.user_name || "the rep"} and managers.</div>;
  }
  if (r.status === "done" && r.overall_score != null) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full border px-2 py-0.5 font-semibold tabular-nums ${scoreTone(r.overall_score)}`}>{r.overall_score}/100</span>
        <Link to={`/recordings/${r.id}`} className="text-blue-400 hover:text-blue-300">View rating →</Link>
      </div>
    );
  }
  if (WORKING.includes(r.status)) {
    const label = ["queued_rating", "scoring"].includes(r.status)
      ? "Picked for this month's review · rating within a few hours"
      : "Picked for this month's review · transcribing (a few minutes)";
    return <div className="flex items-center gap-1.5 text-xs text-blue-300"><Loader2 className="h-3.5 w-3.5 animate-spin" />{label}</div>;
  }
  if (r.status === "transcribed") {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
        Transcribed{data.rating_ready ? "" : " · rating isn't switched on yet"}
        <Link to={`/recordings/${r.id}`} className="text-blue-400 hover:text-blue-300">Transcript →</Link>
        {data.rating_ready && canPick && <button type="button" onClick={() => onRetry(r, "rate")} className="text-blue-400 hover:text-blue-300">Rate</button>}
      </div>
    );
  }
  if (r.status === "error") {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-rose-300">
        <span className="truncate max-w-full">{r.error || "Processing failed"}</span>
        {canPick && (
          <button type="button" onClick={() => onRetry(r, "retry")} className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300">
            <RotateCw className="h-3 w-3" />Try again
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
      Saved. Up to 10 calls per rep each month are picked at random for review.
      {data.transcription_ready && canPick && (
        <button type="button" onClick={() => onRetry(r, "retry")} className="text-blue-400 hover:text-blue-300">Transcribe &amp; rate this one</button>
      )}
    </div>
  );
}

/** Call recordings attached to one consult: play them, add more, remove ones you uploaded. */
export default function ConsultRecordings({ leadId, onChange, onCount }) {
  const askDelete = useDeleteAsk();
  const { effectiveRole, user } = useAuth();
  const canPick = atLeast(effectiveRole || user?.role, "manager");
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const input = useRef(null);

  const load = useCallback(
    () => getLeadRecordings(leadId).then(setData).catch(() => setData({ recordings: [], storage_ready: false })),
    [leadId],
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data) onCount?.(data.recordings.length); }, [data, onCount]);
  // While a recording is being transcribed or rated, check back every 10 seconds.
  const busy = (data?.recordings || []).some((r) => WORKING.includes(r.status));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [busy, load]);

  const retry = async (r, kind) => {
    try {
      // "Re-score" re-runs only what's missing (transcribes first if there's no transcript yet).
      if (kind === "rate" || (data.rating_ready && r.status === "error")) await rescoreRecording(r.id);
      else await retranscribeRecording(r.id);
      toast.success("Started. Transcription takes a few minutes; the rating follows within a few hours.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start it");
    }
  };

  const add = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      await uploadRecording(null, null, file, setProgress, leadId);
      toast.success("Recording attached");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setProgress(null);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async (r) => {
    const reason = askDelete("this recording", `Remove the recording "${r.filename}"?`);
    if (reason === null) return;
    try {
      if (deletionRequested(await deleteRecording(r.id, reason))) return;
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove it");
    }
  };

  if (!data) return <div className="text-xs text-zinc-500">Loading recordings…</div>;
  return (
    <div className="space-y-2" data-testid="consult-recordings">
      {data.recordings.length === 0 && (
        <div className="text-xs text-amber-300/90">No call recording attached to this consult yet.</div>
      )}
      {data.recordings.map((r) => (
        <div key={r.id} className="rounded-lg border border-[#262626] bg-[#0a0a0a] p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <Mic2 className="h-3.5 w-3.5 text-blue-400 shrink-0" />
            <span className="truncate text-zinc-200">{r.filename}</span>
            <span className="text-zinc-500 shrink-0">{[mins(r.duration_seconds) || mb(r.size_bytes), when(r.created_at), r.user_name].filter(Boolean).join(" · ")}</span>
            <button type="button" onClick={() => remove(r)} className="ml-auto p-1.5 text-zinc-500 hover:text-rose-400" aria-label={`Remove ${r.filename}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {!r.private && <audio controls preload="none" src={`${API}/recordings/${r.id}/audio`} className="w-full h-9" />}
          <Progress r={r} data={data} onRetry={retry} canPick={canPick} />
          {r.status === "done" && r.summary && <p className="text-xs text-zinc-400 leading-relaxed">{r.summary}</p>}
        </div>
      ))}
      {data.storage_ready ? (
        <label className={`flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm cursor-pointer
          ${progress != null ? "border-blue-500/50 text-blue-300" : "border-[#333] text-zinc-300 hover:border-blue-500/50"}`}>
          {progress != null ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading… {progress}%</> : <><Upload className="h-4 w-4" /> Add a call recording</>}
          <input ref={input} type="file" accept={AUDIO_ACCEPT} className="hidden" disabled={progress != null}
            onChange={(e) => add(e.target.files?.[0])} data-testid="consult-recording-input" />
        </label>
      ) : (
        <div className="text-xs text-zinc-500">Recording uploads aren&apos;t switched on yet. An admin needs to finish the storage setup.</div>
      )}
    </div>
  );
}
